import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import {
  registerHelperText,
  REGISTRATION_TEXT,
  UPLOAD_PHOTO_TEXT,
} from './lang';
import {
  type StudentParticular,
  useGetStudentParticular,
  useCreateStudentRecord,
} from '../../../graphql';
import { AssessmentAccordion } from '../../../../Assessment';
import {
  Button,
  type FileData,
  PhotoPanel,
  FILE_MANAGE_ORDER,
  Loading,
  Alert,
  type AlertDetails,
} from '../../../../../components';

/* the display may or may not use the user panel */
type StudentPersonalDetailsDisplayProp = {
  studentParticular: StudentParticular;
};

const StudentPersonalDetailsDisplay: React.FunctionComponent<
  StudentPersonalDetailsDisplayProp
> = (props: StudentPersonalDetailsDisplayProp) => {
  const { studentParticular } = props;
  const {
    applicationNumber,
    offeredToYear,
    offeredToSemester,
    programmeCode,
    programmeSubcode,
    HKPFSInd,
    jointPhDProgPartnerInstitution,
    collabPhDProgPartnerInstitution,
    nameInEnglishSurname,
    nameInEnglishGivenName,
    nameInChinese,
    emailAddress,
    dateOfBirth,
    gender,
    nationalityCountryRegion,
    nationalityProvince,
    identityType,
    identityNumber,
    residenceType,
    contactPhoneAreaCode,
    contactPhoneNumber,
    mobileAreaCode,
    mobileNumber,
    addressLine1,
    addressLine2,
    addressLine3,
    addressCountryRegion,
    disabilityType,
    disabilityDetail,
  } = studentParticular;

  const particularMap = [
    [
      { label: REGISTRATION_TEXT.APPLICATION_NO, value: applicationNumber },
      { label: REGISTRATION_TEXT.OFFER_TO_YEAR, value: offeredToYear ?? '-' },
      {
        label: REGISTRATION_TEXT.OFFER_TO_SEM,
        value: offeredToSemester ?? '-',
      },
      { label: REGISTRATION_TEXT.PROG_CODE, value: programmeCode },
      { label: REGISTRATION_TEXT.PROG_SUBCODE, value: programmeSubcode },
      { label: REGISTRATION_TEXT.HKPFS_ID, value: HKPFSInd },

      // temp change: 'jointPhDProgPartnerInstitution' to SRS is actually Dual for GS.
      {
        label: REGISTRATION_TEXT.DUAL_INSTITUTION,
        value: jointPhDProgPartnerInstitution ?? '-',
      },

      // temp change: 'collabPhDProgPartnerInstitution' to SRS is actually Joint for GS.
      {
        label: REGISTRATION_TEXT.JOINT_INSTITUTION,
        value: collabPhDProgPartnerInstitution ?? '-',
      },
    ],
    [
      { label: REGISTRATION_TEXT.ENGLISH_SURNAME, value: nameInEnglishSurname },
      {
        label: REGISTRATION_TEXT.ENGLISH_GIVENNAME,
        value: nameInEnglishGivenName,
      },
      { label: REGISTRATION_TEXT.CHINESE_NAME, value: nameInChinese },
      { label: REGISTRATION_TEXT.EMAIL_ADDRESS, value: emailAddress },
    ],
    [
      { label: REGISTRATION_TEXT.DATE_OF_BIRTH, value: dateOfBirth },
      {
        label: REGISTRATION_TEXT.GENDER,
        value: gender,
      },
      {
        label: REGISTRATION_TEXT.NATIONALITY_REGION,
        value: nationalityCountryRegion,
      },
      {
        label: REGISTRATION_TEXT.NATIONALITY_PROVINCE,
        value: nationalityProvince,
      },
    ],
    [
      { label: REGISTRATION_TEXT.IDENTITY_TYPE, value: identityType },
      {
        label: REGISTRATION_TEXT.IDENTITY_NUMBER,
        value: identityNumber,
      },
      {
        label: REGISTRATION_TEXT.RESIDENCE_TYPE,
        value: residenceType ?? '-',
      },
    ],
    [
      {
        label: REGISTRATION_TEXT.CONTACT_PHONE,
        value:
          contactPhoneAreaCode || contactPhoneNumber
            ? `(+${contactPhoneAreaCode}) ${contactPhoneNumber}`
            : '-',
      },
      {
        label: REGISTRATION_TEXT.MOBILE_AREA_CODE,
        value: mobileAreaCode ?? '-',
      },
      {
        label: REGISTRATION_TEXT.MOBILE_NUMBER,
        value: mobileNumber,
      },
      { label: REGISTRATION_TEXT.ADDRESS_LINE_1, value: addressLine1 ?? '-' },
      {
        label: REGISTRATION_TEXT.ADDRESS_LINE_2,
        value: addressLine2 ?? '-',
      },
      {
        label: REGISTRATION_TEXT.ADDRESS_LINE_3,
        value: addressLine3 ?? '-',
      },
      {
        label: REGISTRATION_TEXT.ADDRESS_COUNTRY_REGION,
        value: addressCountryRegion ?? '-',
      },
    ],
    [
      {
        label: REGISTRATION_TEXT.DISABILITY_TYPE,
        value: disabilityType ?? '-',
      },
      {
        label: REGISTRATION_TEXT.DISABILITY_DETAIL,
        value: disabilityDetail ?? '-',
      },
    ],
  ];
  return (
    <div className="p-4 mt-2">
      {particularMap.map((arr, arrIndex) => (
        <div key={arrIndex}>
          {arr.map((item, index) => {
            const { label, value } = item;
            return (
              <div
                key={index}
                className="grid grid-cols-[7fr,5fr] justify-items-start items-start mx-8 text-sm"
              >
                <div className="font-semibold mt-1">{label}</div>
                <div>{value}</div>
              </div>
            );
          })}
          <hr className="m-4 h-[0.1rem] bg-neutral-300" />
        </div>
      ))}
      <h2
        dangerouslySetInnerHTML={{
          __html: REGISTRATION_TEXT.REMARK,
        }}
        className="font-semibold mt-8 text-sm text-[#951D1D] text-center"
      />
    </div>
  );
};

type UploadStudentCardPhotoProp = {
  disabled: boolean;
  photoFile?: FileData;
  onPhotoBaseChange: (newBase: string) => void;
  onPhotoChange: (file: FileData | null) => void;
};

const UploadStudentCardPhoto: React.FunctionComponent<
  UploadStudentCardPhotoProp
> = (props: UploadStudentCardPhotoProp) => {
  const { photoFile, disabled, onPhotoBaseChange, onPhotoChange } = props;
  const { applicationId } = useParams();

  function handleFilesChange(order: number, id: string): void {
    switch (order) {
      /** reupload */
      case FILE_MANAGE_ORDER.UPLOAD:
        onPhotoChange({
          id,
          name: `${applicationId}_student_card_photo`,
          link: '',
        } as FileData);
        break;
      case FILE_MANAGE_ORDER.REMOVE:
        onPhotoChange(null);
        break;
      default:
        break;
    }
  }

  return (
    <div className="px-8">
      <div className="grid grid-cols-[10fr,3fr] gap-2 justify-items-start items-center py-2 px-4 gap-4">
        <RequirementBox className="flex flex-col text-sm">
          {UPLOAD_PHOTO_TEXT.map((requirement, index) => (
            <div
              dangerouslySetInnerHTML={{
                __html: `${requirement}`,
              }}
              key={index}
              className="mb-1"
            />
          ))}
        </RequirementBox>
        <PhotoPanel
          onPhotoBaseChange={onPhotoBaseChange}
          caption={REGISTRATION_TEXT.STUDENT_PHOTO}
          files={photoFile}
          handleFilesChange={handleFilesChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

type CreateStudentRecordProp = {
  refetchApplicationNum: () => void;
  handleJumpNext: () => void;
};

export const CreateStudentRecord: React.FunctionComponent<
  CreateStudentRecordProp
> = (props: CreateStudentRecordProp) => {
  const { applicationId = '' } = useParams();
  const { refetchApplicationNum, handleJumpNext } = props;
  const [photoBase64, setPhotoBase64] = useState<string>('');
  const [openAlert, setOpenAlert] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });
  const { studentParticular, loading, error, refetch } =
    useGetStudentParticular(applicationId);

  const { photoFile, studentNumber, offeredToYear, offeredToSemester } =
    studentParticular;

  const [photo, setPhoto] = useState<FileData | null>(photoFile ?? null);
  const ifRegistered = Boolean(studentNumber);

  // TODO: get Entry Year options from backend
  const convertEntryYearMap: Record<string, string> = {
    '2023-03': '30/04/2024',
    '2024-01': '31/08/2024',
    '2024-02': '31/12/2024',
    '2024-03': '30/04/2025',
    '2025-01': '31/08/2025',
  };

  useEffect(() => {
    if (!loading && !!photoFile) setPhoto(photoFile);
  }, [loading]);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handlePhotoBaseChange = (newBase: string): void => {
    setPhotoBase64(newBase);
  };
  const handlePhotoChange = (file: FileData | null): void => {
    setPhoto(file);
  };

  const createStudentRecord = useCreateStudentRecord();
  const handleRegister = async (): Promise<void> => {
    if (photo?.id === undefined) {
      setAlertDetails({
        alertContent: REGISTRATION_TEXT.PHOTO_ALERT,
        alertType: 'warning',
      });
      setOpenAlert(true);
      return;
    }
    setCreateLoading(true);
    try {
      const variables = {
        input: {
          applicationId,
          photoFile: photo,
          photoBase64,
        },
      };

      const res = await createStudentRecord({
        variables,
      });
      if (res.data?.createStudentRecord?.applicationId !== undefined) {
        setAlertDetails({
          alertContent: 'Created Student Record Successfully.',
          alertType: 'success',
        });
        setOpenAlert(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        refetch();
        refetchApplicationNum();
      }
    } catch (e) {
      setAlertDetails({
        alertContent: `Error Occurs.` + `${(e as Error).message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
    setCreateLoading(false);
  };

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div
        className="px-2 py-4 mr-2"
        dangerouslySetInnerHTML={{
          __html: registerHelperText(
            convertEntryYearMap[offeredToYear + '-' + offeredToSemester],
          ),
        }}
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="pb-12">
          {createLoading && <Loading />}
          <AssessmentAccordion
            title={REGISTRATION_TEXT.CONFIRM_PERSONAL_DETAILS}
            InfoDetails={
              <StudentPersonalDetailsDisplay
                studentParticular={studentParticular}
              />
            }
            defaultExpanded
          />
          <AssessmentAccordion
            title={REGISTRATION_TEXT.UPLOAD_PHOTO}
            InfoDetails={
              <UploadStudentCardPhoto
                photoFile={photo ?? undefined}
                onPhotoBaseChange={handlePhotoBaseChange}
                onPhotoChange={handlePhotoChange}
                disabled={ifRegistered}
              />
            }
            defaultExpanded
          />
          <div className="flex justify-center pt-12">
            {ifRegistered ? (
              <div className="flex flex-col justify-center self-center">
                <div>{REGISTRATION_TEXT.REGISTERED}</div>
                <div className="self-center">
                  <Button
                    variant="text"
                    onClick={handleJumpNext}
                    sx={{ textTransform: 'none' }}
                  >
                    {REGISTRATION_TEXT.NEXT_STEP}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="contained" onClick={handleRegister}>
                {REGISTRATION_TEXT.REGISTER}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RequirementBox = styled.div`
  .req-ref-link {
    color: #951d1d;
    cursor: pointer;
    font-size: 14px;
    text-decoration: underline;
    font-style: normal;
  }
`;
